import ScormAPI from './scorm-api.js';

/**
 * SCORM MANAGER
 * Conecta la lógica del juego (sesiones y puntuaciones) con la API SCORM.
 */
const ScormManager = {
    isLMS: false,
    startTime: null,

    init() {
        this.isLMS = ScormAPI.init();
        
        if (this.isLMS) {
            this.startTime = new Date();
            
            // Si es la primera vez que entra, marcar como incompleto
            const status = ScormAPI.get('cmi.core.lesson_status');
            if (status === 'not attempted' || status === 'unknown' || status === '') {
                ScormAPI.set('cmi.core.lesson_status', 'incomplete');
                ScormAPI.commit();
            }

            // Registrar el nombre del alumno si el LMS lo provee
            const studentName = ScormAPI.get('cmi.core.student_name');
            if (studentName) {
                console.log(`🎓 SCORM: Alumno detectado: ${studentName}`);
            }

            // Manejar cierre de ventana inesperado
            window.addEventListener('beforeunload', () => {
                this.finish();
            });
        }
    },

    /**
     * Actualiza la puntuación del alumno (0 a 100)
     * @param {number} score 
     */
    updateScore(score) {
        if (!this.isLMS) return;
        
        // Asegurarse de que el score es un número entre 0 y 100 (Estándar SCORM 1.2)
        const safeScore = Math.min(Math.max(Math.round(score), 0), 100);
        
        console.log(`📊 SCORM: Enviando puntuación -> ${safeScore}`);
        ScormAPI.set('cmi.core.score.raw', safeScore);
        ScormAPI.set('cmi.core.score.min', '0');
        ScormAPI.set('cmi.core.score.max', '100');
        
        ScormAPI.commit(); // Forzar sincronización
    },

    /**
     * Marca la actividad como completada
     * Idealmente se llama cuando el alumno termina los problemas asignados
     */
    complete() {
        if (!this.isLMS) return;
        
        console.log('🏆 SCORM: Marcando lección como completada');
        ScormAPI.set('cmi.core.lesson_status', 'completed');
        ScormAPI.commit();
    },

    /**
     * Termina la sesión, calcula el tiempo y se despide del LMS
     */
    finish() {
        if (!this.isLMS) return;

        // Calcular y enviar tiempo de sesión (Formato: HH:MM:SS.SS)
        if (this.startTime) {
            const endTime = new Date();
            const sessionTime = this.formatScormTime(endTime.getTime() - this.startTime.getTime());
            ScormAPI.set('cmi.core.session_time', sessionTime);
        }

        // Si la cerramos y no estaba "completed", la dejamos en "incomplete" (automático)
        
        ScormAPI.finish();
        this.isLMS = false;
    },

    /**
     * Convierte milisegundos a formato SCORM (HH:MM:SS.SS)
     */
    formatScormTime(ms) {
        const totalSeconds = Math.floor(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        
        const h = hours.toString().padStart(4, '0');
        const m = minutes.toString().padStart(2, '0');
        const s = seconds.toString().padStart(2, '0');
        
        return `${h}:${m}:${s}.00`;
    },

    /**
     * Generador de declaraciones xAPI (Tin Can) para analíticas pedagógicas avanzadas
     * @param {string} verb - Acción xAPI ('answered', 'completed', 'experienced')
     * @param {string} activityId - Identificador único de la actividad o problema
     * @param {Object} resultData - Resultado ({ score: 0.95, success: true, duration: 'PT15S' })
     */
    sendXAPIStatement(verb, activityId, resultData = {}) {
        const studentName = ScormAPI.get('cmi.core.student_name') || 'Alumno Detectimáticas';
        
        const statement = {
            actor: {
                name: studentName,
                objectType: 'Agent'
            },
            verb: {
                id: `http://adlnet.gov/expapi/verbs/${verb}`,
                display: { 'es-ES': verb, 'en-US': verb }
            },
            object: {
                id: `https://detectimaticas.edu/activities/${activityId}`,
                definition: {
                    name: { 'es-ES': `Problema Matemático ${activityId}` },
                    description: { 'es-ES': 'Resolución de problema gamificado LOMLOE' }
                },
                objectType: 'Activity'
            },
            result: resultData,
            timestamp: new Date().toISOString()
        };

        console.log('📡 xAPI Statement generado:', statement);
        
        // Dispatch custom event for LRS forwarders
        if (typeof document !== 'undefined') {
            document.dispatchEvent(new CustomEvent('xapi:statement', { detail: statement }));
        }
        return statement;
    },

    /**
     * Registra un error categorizado por fase pedagógica (Analítica Docente Avanzada)
     * @param {number} step - Paso del 1 al 6
     * @param {'COMPREHENSION_ERROR'|'REPRESENTATION_ERROR'|'OPERATION_SELECTION_ERROR'|'CALCULATION_ERROR'} errorType 
     * @param {object} details 
     */
    recordCategorizedError(step, errorType, details = {}) {
        const errorRecord = {
            step,
            errorType,
            details,
            timestamp: new Date().toISOString()
        };

        // Guardar localmente para analítica del dashboard docente
        try {
            const raw = localStorage.getItem('detectimaticas_error_analytics');
            let existing = [];
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) existing = parsed;
            }
            existing.push(errorRecord);
            localStorage.setItem('detectimaticas_error_analytics', JSON.stringify(existing.slice(-100)));
        } catch (_e) { /* ignore localStorage restriction errors */ }

        // Emitir sentencia xAPI de fallo estructurado
        return this.sendXAPIStatement('failed', `step_${step}_${errorType.toLowerCase()}`, {
            success: false,
            extensions: {
                'https://detectimaticas.edu/xapi/ext/error_type': errorType,
                'https://detectimaticas.edu/xapi/ext/step': step
            }
        });
    },

    // Alias for backward compatibility
    sendxAPIStatement(...args) {
        return this.sendXAPIStatement(...args);
    }
};

if (typeof window !== 'undefined') {
    window.Detectimaticas = window.Detectimaticas || {};
    window.Detectimaticas.ScormManager = ScormManager;
    window.ScormManager = ScormManager;
}
export default ScormManager;
